import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  useTheme,
  Tabs,
  Tab,
  Fade
} from '@mui/material';
import {
  Stars as StarsIcon,
  Storage as StorageIcon,
  RocketLaunch as RocketLaunchIcon,
  QueryStats as QueryStatsIcon,
  RestartAlt as RestartAltIcon,
  Terminal as TerminalIcon,
  DeleteSweep as DeleteSweepIcon,
  ManageAccounts as ManageAccountsIcon,
  Description as DescriptionIcon,
  NetworkCheck as NetworkCheckIcon,
  Build as BuildIcon,
  Security as SecurityIcon,
  SettingsSuggest as SettingsSuggestIcon,
  Speed as SpeedIcon,
  TapAndPlay,
  WifiCallingOutlined,
} from '@mui/icons-material';
import { AdminTemplate } from '../components';

type Category = "Tümü" | "Sistem" | "Güvenlik" | "Yönetim" | "Performans";

interface Tool {
  title: string;
  description: string;
  icon: JSX.Element;
  action: string;
  category: Category;
  path?: string;
}

export const InternalTools = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<Category>("Tümü");

  const categories: { label: Category; icon: JSX.Element }[] = [
    { label: "Tümü", icon: null },
    { label: "Fraud Yönetimi", icon: <TapAndPlay fontSize="small" /> }
  ];

  const tools: Tool[] = [
    {
      title: "IP Kısıtlama",
      description: "Şüpheli veya yetkisiz IP adreslerine karşı sistemlerinizi korumak için gelişmiş IP kısıtlama yönetimi.",
      icon: <WifiCallingOutlined fontSize="large" />,
      action: "Sayfaya Git",
      url: '/fraud-management/ip-control',
      category: "Fraud Yönetimi"
    }
  ];

  const filteredTools = selectedCategory === "Tümü"
    ? tools
    : tools.filter(tool => tool.category === selectedCategory);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: Category) => {
    setSelectedCategory(newValue);
  };

  return (
    <AdminTemplate headerProps={{ headerTitle: "Araçlar Dizini", hideAddButton: true }}>
      <Box p={3}>
        {/* Header Section */}
        <Box mb={4}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Dahili Araçlar
          </Typography>
          <Typography variant="body1" color="text.secondary">
            İdari ve teknik yardımcı araçlar için merkezi yönetim paneli.
          </Typography>
        </Box>

        {/* Featured Tool Section */}
        <Box mb={6}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <StarsIcon color="primary" />
            <Typography variant="h5" fontWeight="bold">
              Öne Çıkan Araç
            </Typography>
          </Stack>

          <Card variant="outlined" sx={{ borderRadius: 3, border: `2px solid ${theme.palette.primary.light}` }}>
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={3} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        flexShrink: 0
                      }}
                    >
                      <StorageIcon fontSize="large" />
                    </Box>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <Typography variant="h5" fontWeight="bold">
                          BIN Sorgulama
                        </Typography>
                        <Chip
                          label="YENİ"
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 'bold', borderRadius: 1 }}
                        />
                      </Stack>
                      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600 }}>
                        Kart numarasının ilk 6-8 hanesini girerek kart markasını, tipini ve düzenleyen banka bilgilerini görüntüleyin.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<RocketLaunchIcon />}
                        sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 'bold' }}
                        onClick={() => navigate('/internal-tools/bin-query')}
                      >
                        BIN Sorgulama'yı Başlat
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 200,
                      height: 128,
                      bgcolor: 'action.hover',
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.disabled'
                    }}
                  >
                    <QueryStatsIcon sx={{ fontSize: 64 }} />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {/* Directory Section */}
        <Box>
          <Typography variant="h5" fontWeight="bold" mb={3}>
            Dizin
          </Typography>

          {/* Category Filter Tabs */}
          <Box sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="tool categories"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  minHeight: 48,
                  px: 3
                }
              }}
            >
              {categories.map((cat) => (
                <Tab
                  key={cat.label}
                  value={cat.label}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {cat.icon}
                      <span>{cat.label}</span>
                    </Stack>
                  }
                />
              ))}
            </Tabs>
          </Box>

          <Grid container spacing={3}>
            {filteredTools.map((tool, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in={true} timeout={500 + (index * 100)}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-4px)',
                        boxShadow: 2
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'action.hover',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                          }}
                        >
                          {React.cloneElement(tool.icon, { color: 'inherit' })}
                        </Box>
                        <Chip
                          label={tool.category}
                          size="small"
                          variant="outlined"
                          color="default"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24,
                            borderColor: 'divider',
                            bgcolor: 'background.paper'
                          }}
                        />
                      </Stack>

                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {tool.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={3} flex={1}>
                        {tool.description}
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        onClick={() => {
                          if (tool.url) {
                            navigate(tool.url);
                          }
                        }}
                      >
                        {tool.action}
                      </Button>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </AdminTemplate>
  );
};